/** Cualquier reporte tabular: libros, balance, IVA, anexos, excepciones.
 *
 * Los que declaran expansión —Balance de Comprobación y Libro Mayor— ofrecen
 * además el árbol del plan de cuentas de Odoo. Ahí la jerarquía de
 * `account.group` SÍ es la estructura del documento, porque el documento ES el
 * plan de cuentas; en un estado financiero nunca lo es.
 *
 * El Balance de Comprobación ABRE en ese árbol, porque su XLSX y su PDF llevan
 * subtotales por grupo y negritas: abrir la pantalla como tabla plana dejaba la
 * vista y el documento con dos formas distintas. Lo declara el servidor con
 * `opens_as_tree`, no una lista de claves en el cliente. La tabla plana no
 * desaparece —sirve para buscar una cuenta, ordenar por saldo y copiar— y las
 * dos vistas se eligen con un selector.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { Config, SessionInfo } from "../api/types";
import { ChartTree } from "../components/ChartTree";
import { DataTable } from "../components/DataTable";
import { PeriodPicker } from "../components/PeriodPicker";
import { defaultPeriod } from "../components/period";
import { ErrorState, Loading } from "../components/States";
import { formatInteger } from "../components/format";
import { ExportMenu } from "../components/ExportMenu";

const PAGE_SIZE = 200;

/**
 * Los anexos del F-07 que la plataforma produce.
 *
 * El de Documentos Anulados y Extraviados NO está: su estructura no se ha
 * cotejado contra el manual del Ministerio, y un archivo que se sube al portal
 * no se ofrece a medias. Que falte se ve; que esté mal, no.
 */
const ANEXOS_F07 = [
  { numero: "1", nombre: "Ventas a Contribuyentes" },
  { numero: "2", nombre: "Ventas a Consumidor Final" },
  { numero: "3", nombre: "Compras" },
  { numero: "5", nombre: "Compras a Sujetos Excluidos" },
  /* `sinValidar` NO es decoración. Los cuatro de arriba están cotejados columna
     a columna contra el manual del Ministerio; este no, y de sus diez columnas
     tres salen vacías porque Odoo no guarda el dato. Quien descarga un archivo
     para subirlo al portal de Hacienda tiene que saberlo ANTES de descargarlo,
     no después de que se lo rechacen. Ver `ANEXOS_SIN_VALIDAR` en
     `reports/f07_csv.py`. */
  {
    numero: "anulados",
    nombre: "Documentos Anulados y Extraviados",
    sinValidar: true,
  },
] as const;

export function Report({
  config,
  session,
}: {
  config: Config;
  session: SessionInfo;
}) {
  const { reportKey = "" } = useParams();
  const [period, setPeriod] = useState(defaultPeriod);
  const [offset, setOffset] = useState(0);
  const spec = config.reports.find((report) => report.key === reportKey);
  const [arbol, setArbol] = useState(spec?.tree?.opens_as_tree ?? false);
  const { data, error, loading } = useAsync(
    () => (arbol ? Promise.resolve(null) : api.report(reportKey, period, PAGE_SIZE, offset)),
    [reportKey, period.periodFrom, period.periodTo, offset, arbol],
  );

  if (!spec) {
    return (
      <div className="state">
        <h2>Reporte desconocido</h2>
        <p>No hay ningún reporte con la clave «{reportKey}».</p>
      </div>
    );
  }

  const total = data?.total ?? 0;
  const shown = data?.rows.length ?? 0;
  // Solo tiene sentido pedir «tabla plana» donde hay otra presentación de la
  // que distinguirla: en los demás reportes la descarga YA es la tabla.
  const tablaPlana = !arbol && (spec.tree?.opens_as_tree ?? false);

  /* Los controles son los MISMOS en las tres vistas —jerarquía, tabla y estado
     de carga—, así que se declaran una vez. En la tabla van montados DENTRO de
     la caja, sobre la cabecera navy; en el árbol van sueltos, porque ahí no hay
     caja a la que pertenecer. Sueltos sobre la tabla, la relación entre el
     filtro y lo que filtra quedaba al criterio de quien mirara. */
  const controles = (
    <PeriodPicker
      value={period}
      onChange={(next) => {
        setOffset(0);
        setPeriod(next);
      }}
    >
      {/* Un solo control, igual que en el cierre: el reporte y el árbol de
          auditoría salían de dos sitios distintos de esta misma pantalla.
          La descarga sigue a la vista —en jerarquía baja el documento con
          subtotales y firmas; en tabla, la tabla—, y el rótulo lo dice en vez
          de dejarlo a la sorpresa. */}
      <ExportMenu
        /* Los CSV del F-07 solo en su pantalla: son variantes DEL documento
           que se está viendo. El Comprobante de Partidas estaba aquí y ya no:
           tiene entrada propia, porque no era una variante del Libro Diario
           sino otro documento. */
        archivos={
          spec.key === "f07_annexes"
            ? {
                label: "Anexos del F-07 · CSV para el portal",
                note:
                  "Un archivo por anexo, con el formato del Ministerio de Hacienda: " +
                  "punto y coma, sin encabezados y con la fecha de emisión de cada documento.",
                archivos: ANEXOS_F07.map((anexo) => ({
                  label:
                    anexo.numero === "anulados"
                      ? anexo.nombre
                      : `Anexo ${anexo.numero} · ${anexo.nombre}`,
                  note:
                    "sinValidar" in anexo
                      ? "Pendiente de validación del contador: estructura sin " +
                        "cotejar contra el manual, y tres columnas vacías porque " +
                        "Odoo no guarda fecha ni motivo de anulación."
                      : undefined,
                  href: api.f07AnexoUrl(anexo.numero, period),
                })),
              }
            : undefined
        }
        documento={{
          label: tablaPlana ? `${spec.title} · tabla plana` : spec.title,
          note: tablaPlana
            ? "La tabla que está viendo, para ordenar por saldo y copiar."
            : undefined,
          xlsx: api.downloadUrl(spec.key, "xlsx", period, tablaPlana),
          /* «Tabla plana» vale para los DOS formatos. Iba solo al Excel, así
             que dos botones a ocho píxeles, con el mismo rótulo y el mismo
             período, entregaban documentos con forma distinta. */
          pdf: api.downloadUrl(spec.key, "pdf", period, tablaPlana),
        }}
        auditoria={
          spec.tree
            ? {
                reportKey: spec.key,
                period,
                levels: spec.tree.export_levels,
                mayReadJournal:
                  !session.permissions || session.permissions.includes("journal:read"),
              }
            : undefined
        }
      />
      {/* Un selector y no un botón que alterna: con dos vistas legítimas, el
          rótulo de un botón nombra la que NO se está viendo, y eso se lee
          como el título de la pantalla. */}
      {spec.tree && (
        <div className="switch" role="group" aria-label="Forma de la vista">
          <button
            type="button"
            className={arbol ? "switch-on" : undefined}
            aria-pressed={arbol}
            onClick={() => setArbol(true)}
          >
            Jerarquía
          </button>
          <button
            type="button"
            className={arbol ? undefined : "switch-on"}
            aria-pressed={!arbol}
            onClick={() => setArbol(false)}
          >
            Tabla
          </button>
        </div>
      )}
    </PeriodPicker>
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{spec.title}</h1>
          {spec.aliases.length > 0 && <p>También denominado: {spec.aliases.join(", ")}</p>}
        </div>
      </div>


      {arbol && spec.tree && (
        <>
          {controles}
          <ChartTree reportKey={spec.key} spec={spec.tree} period={period} session={session} />
        </>
      )}

      {!arbol && (loading || error) && (
        <>
          {controles}
          {loading && <Loading what={spec.title.toLowerCase()} />}
          {error && <ErrorState error={error} />}
        </>
      )}

      {!arbol && data && (
        <>
          <DataTable
            columns={spec.columns}
            rows={data.rows}
            totalRow={data.total_row}
            toolbar={controles}
            footer={
              <>
                <span className="small muted">
                  {/* Con la página vacía el rango salía «1–0 de 0 filas», que no
                      existe. Se dice el hecho en vez de imprimir un intervalo
                      imposible. */}
                  {shown === 0
                    ? "Sin filas para el período seleccionado"
                    : `${formatInteger(offset + 1)}–${formatInteger(offset + shown)} de ${formatInteger(total)} filas`}
                </span>
                <div className="spacer" />
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                >
                  Anterior
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + shown >= total}
                >
                  Siguiente
                </button>
              </>
            }
          />
          <p className="small muted">
            La vista pagina; la descarga trae el reporte completo con su procedencia y,
            mientras el mapping siga en borrador, con la marca de validación pendiente.
          </p>
        </>
      )}
    </>
  );
}
